import RatingReview from '@shared/components/core/rating/RatingReview';
import RatingSummary from '@shared/components/core/rating/RatingSummary';

/** Minimum number of ratings before an aggregate average is shown (avoids noisy 1-off ratings). */
export const MIN_PUBLIC_RATINGS = 5;

interface ExtensionMarketplaceStatsProps {
    /** Display name of the extension, used to title the review dialog. */
    title?: string;
    /** Distinct users who have this extension enabled (global). Omit to leave the figure out entirely. */
    installCount?: number;
    /** Average public star rating. */
    averageRating?: number;
    /** Number of public star ratings. */
    ratingCount?: number;
    /** The caller's own current rating, pre-filled in the review dialog. */
    myRating?: number;
    /**
     * Figures only, without the "installs" wording. Defaults to true: every surface using this is a
     * card, where the spelled-out labels crowd out the name and description.
     */
    compact?: boolean;
    /** Show empty stars when nothing has been rated yet, so every card in a grid has the same shape. */
    showEmptyRating?: boolean;
    /**
     * When provided, the row opens the review dialog and reports the chosen rating. When omitted, the
     * row is the read-only aggregate average — shown only once there are at least
     * {@link MIN_PUBLIC_RATINGS} ratings.
     */
    onRate?: (rating: number) => void | Promise<void>;
}

/**
 * Public-marketplace stats for a skill/connector: the average score, stars, and install count. Thin
 * adapter over the shared {@link RatingSummary} / {@link RatingReview} pair, so marketplace cards,
 * skills, and connectors all read the same as every other rateable surface.
 *
 * Two deliberate narrowings for the public marketplace:
 * - No comment field. The public rating endpoint takes a score only (`RateAgentExtensionRequest`);
 *   comments live on the org-scoped endpoints, so they're collected in the org feedback dialog.
 * - No comment count. Comments are org-internal, so the public figure would be misleading here.
 */
export default function ExtensionMarketplaceStats({
    title,
    installCount,
    averageRating,
    ratingCount,
    myRating,
    compact = true,
    showEmptyRating = true,
    onRate,
}: ExtensionMarketplaceStatsProps) {
    if (!onRate) {
        return (
            <RatingSummary
                averageRating={averageRating}
                ratingCount={ratingCount}
                installCount={installCount}
                minRatings={MIN_PUBLIC_RATINGS}
                compact={compact}
                showEmptyRating={showEmptyRating}
            />
        );
    }

    return (
        <RatingReview
            subject={title ?? ''}
            averageRating={averageRating}
            ratingCount={ratingCount}
            myRating={myRating}
            minRatings={MIN_PUBLIC_RATINGS}
            compact={compact}
            showEmptyRating={showEmptyRating}
            hideComment
            onSubmit={({ rating }) => onRate(rating)}
        />
    );
}
